import { Form, FormPayload, ListPayload, Template, PreviewPageType} from '@dev/ee-api/ee-api-models';
import { EditorSaveData, EditorSaveError } from '@dev/template-editor';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { Step } from '@/components/FormCreator/types';
import { ApplicationState } from '..';
import {
    FormCreatorActionTypes,
    CloseAction,
    EditorFile,
    FilesType,
    TemplateSaveStartAction,
    TemplateSaveErrorAction,
    TemplateSaveSuccessAction,
    SetDefaultTemplateAction,
    TemplateLoadStartAction,
    TemplateLoadSuccessAction,
    TemplateLoadErrorAction,
    ResetAction,
    TemplateIncrementAutosaveCounterAction,
    TemplateUploadFileAction,
    TemplateUploadFileStartAction,
    TemplateUploadFileErrorAction,
    TemplateUploadFileSuccessAction,
    FormCreatorState,
    SetIsTemplateOpenAction,
    TemplateShowSaveErrorsAction,
    TemplateChangedAction,
    OpenUpsellingOverlayAction,
    CloseUpsellingOverlayAction,
    ModifyFormAction,
    LoadSuccessAction,
    LoadStartAction,
    LoadErrorAction,
    UpdateContactListsAction,
    SaveStartAction,
    SaveSuccessAction,
    SaveErrorAction,
    CancelEditStepAction,
    StartEditStepAction,
    AddContactListStartAction,
    AddContactListErrorStatusAction,
    SetTemplatePreviewAction,
    OpenGalleryAction,
    CloseGalleryAction,
    TemplateDeselectAction,
    LoadPageTemplateStartAction,
    LoadPageTemplateSuccessAction,
    LoadPageTemplateErrorAction,
    TemplateOpenFromGalleryAction,
    FormErrors,
    SetFormErrorsAction,
    TemplatePreviewFromGalleryAction,
    SetPopupDelayAction,
    SetPopupTriggerAction,
    FormPopupTriggerEnum,
    SaveSettings,
    UpdateSaveSettingsAction,
} from './types';

export const LoadStart = (name: string): LoadStartAction => ({
    type: FormCreatorActionTypes.LOAD_START,
    payload: { name },
});

export const LoadSuccess = (
    formObject: Form
): LoadSuccessAction => ({
    type: FormCreatorActionTypes.LOAD_SUCCESS,
    payload: { formObject },
});

export const LoadError = (): LoadErrorAction => ({
    type: FormCreatorActionTypes.LOAD_ERROR,
});

export const LoadPageTemplateStart = (pageTemplateType: PreviewPageType): LoadPageTemplateStartAction => ({
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_START,
    payload: { pageTemplateType },
});

export const LoadPageTemplateSuccess = (
    pageTemplateType: PreviewPageType,
    pageTemplate: string
): LoadPageTemplateSuccessAction => ({
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS,
    payload: { pageTemplateType, pageTemplate },
});

export const LoadPagePreviewError = (): LoadPageTemplateErrorAction => ({
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_ERROR,
});

export const SaveStart = (isPopupCreator?: boolean): SaveStartAction => ({
    type: FormCreatorActionTypes.SAVE_START,
    payload: { isPopupCreator },
});

export const SaveSuccess = (
    formObject: Form
): SaveSuccessAction => ({
    type: FormCreatorActionTypes.SAVE_SUCCESS,
    payload: { formObject },
});

export const SaveError = (): SaveErrorAction => ({
    type: FormCreatorActionTypes.SAVE_ERROR
});

export const SetFormErrors = (errors?: FormErrors): SetFormErrorsAction => ({
    type: FormCreatorActionTypes.SET_FORM_ERRORS,
    payload: {
        errors
    }
});

export const ModifyForm = (formPayload: Partial<FormPayload>): ModifyFormAction => ({
    type: FormCreatorActionTypes.MODIFY_FORM,
    payload: {
        formPayload
    }
});

export const StartEditStep = (stepInEdit: Step): StartEditStepAction => ({
    type: FormCreatorActionTypes.START_EDIT_STEP,
    payload: { stepInEdit }
});

export const CancelEditStep = (): CancelEditStepAction => ({
    type: FormCreatorActionTypes.CANCEL_EDIT_STEP,
});

export const Close = (): CloseAction => ({
    type: FormCreatorActionTypes.CLOSE,
});

export const TemplateUploadFile = (file: File, blockId: string): TemplateUploadFileAction => ({
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE,
    payload: {
        file,
        blockId,
    },
});

export const TemplateUploadFileStart = (): TemplateUploadFileStartAction => ({
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START,
});

export const  TemplateUploadFileSuccess = (filesList: EditorFile[]): TemplateUploadFileSuccessAction => ({
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS,
    payload: {
        filesList,
    },
});

export const  TemplateUploadFileError = (): TemplateUploadFileErrorAction => ({
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR,
});


export const SetDefaultTemplate = ({ templateObject }: { templateObject: Template }): SetDefaultTemplateAction => ({
    type: FormCreatorActionTypes.SET_DEFAULT_TEMPLATE,
    payload: { templateObject },
});

export const TemplateLoadStart = (name?: string, isPublic = false, useDraftScope = false): TemplateLoadStartAction => ({
    type: FormCreatorActionTypes.TEMPLATE_LOAD_START,
    payload: { name, isPublic, useDraftScope },
});

export const TemplateLoadSuccess = (
    templateObject: Template,
    isPublic: boolean,
    useDraftScope: boolean
): TemplateLoadSuccessAction => ({
    type: FormCreatorActionTypes.TEMPLATE_LOAD_SUCCESS,
    payload: { templateObject, isPublic, useDraftScope },
});

export const TemplateLoadError = (): TemplateLoadErrorAction => ({
    type: FormCreatorActionTypes.TEMPLATE_LOAD_ERROR,
});

export const TemplateIncrementAutosaveCounter = (): TemplateIncrementAutosaveCounterAction => ({
    type: FormCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER,
});

export const TemplateSaveStart = ({
    data,
    exitOnSave,
    errors
}: {
    data: EditorSaveData;
    exitOnSave: boolean;
    errors?: EditorSaveError[]
}): TemplateSaveStartAction => ({
    type: FormCreatorActionTypes.TEMPLATE_SAVE_START,
    payload: { data, exitOnSave, errors },
    meta: {
        thunk: true
    }
});

export const TemplateSaveSuccess = (data: EditorSaveData): TemplateSaveSuccessAction => ({
    type: FormCreatorActionTypes.TEMPLATE_SAVE_SUCCESS,
    payload: {
        data,
    }
});

export const TemplateSaveError = (nameError?): TemplateSaveErrorAction => ({
    type: FormCreatorActionTypes.TEMPLATE_SAVE_ERROR,
    payload: { nameError },
});

export const SetIsTemplateOpen = (isOpen: boolean, meta?: Record<string, unknown>): SetIsTemplateOpenAction => ({
    type: FormCreatorActionTypes.SET_IS_TEMPLATE_OPEN,
    payload: { isOpen },
    meta
});

export const SetTemplatePreview = (content?: string, name?: string, isPublic?: boolean): SetTemplatePreviewAction => ({
    type: FormCreatorActionTypes.SET_TEMPLATE_PREVIEW,
    payload: { content, name, isPublic },
});

export const Reset = (): ResetAction => ({
    type: FormCreatorActionTypes.RESET,
});

export const TemplateShowSaveErrors = (): TemplateShowSaveErrorsAction => ({
    type: FormCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS,
});

export const TemplateChanged = (): TemplateChangedAction => ({
    type: FormCreatorActionTypes.TEMPLATE_CHANGED,
});

export const TemplateDeselect = (): TemplateDeselectAction => ({
    type: FormCreatorActionTypes.TEMPLATE_DESELECT,
});

export const OpenUpsellingOverlay = (feature: AvailableFeaturesUpselling): OpenUpsellingOverlayAction => ({
    type: FormCreatorActionTypes.OPEN_UPSELLING_OVERLAY,
    payload: {
        feature,
    },
});

export const CloseUpsellingOverlay = (): CloseUpsellingOverlayAction => ({
    type: FormCreatorActionTypes.CLOSE_UPSELLING_OVERLAY,
});

export const UpdateContactLists = (contactLists: string[]): UpdateContactListsAction => ({
    type: FormCreatorActionTypes.UPDATE_CONTACT_LISTS,
    payload: {
        contactLists,
    },
});

export const AddContactListStart = (list: ListPayload): AddContactListStartAction => ({
    type: FormCreatorActionTypes.ADD_CONTACT_LIST_START,
    payload: {
        list,
    },
});

export const AddContactListErrorStatus = (isAddListError: boolean): AddContactListErrorStatusAction => ({
    type: FormCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS,
    payload: {
        isAddListError
    }
});

export const OpenGallery = (): OpenGalleryAction => ({
    type: FormCreatorActionTypes.OPEN_GALLERY,
});

export const CloseGallery = (): CloseGalleryAction => ({
    type: FormCreatorActionTypes.CLOSE_GALLERY,
});


export const TemplatePreviewFromGallery = (
    templateName?: string,
    isPublic?: boolean,
): TemplatePreviewFromGalleryAction => ({
    type: FormCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY,
    payload: {
        templateName,
        isPublic,
    },
});

export const TemplateOpenFromGallery = (
    templateName?: string,
    isPublic?: boolean,
): TemplateOpenFromGalleryAction => ({
    type: FormCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY,
    payload: {
        templateName,
        isPublic,
    },
});

export const SetPopupDelay = (popupDelay: number): SetPopupDelayAction => ({
    type: FormCreatorActionTypes.SET_POPUP_DELAY,
    payload: { popupDelay },
});

export const SetPopupTrigger = (popupTrigger: FormPopupTriggerEnum): SetPopupTriggerAction => ({
    type: FormCreatorActionTypes.SET_POPUP_TRIGGER,
    payload: { popupTrigger },
});

export const UpdateSaveSettings = (settings: SaveSettings): UpdateSaveSettingsAction => ({
    type: FormCreatorActionTypes.UPDATE_SAVE_SETTINGS,
    payload: settings,
});

export const getFormCreatorStore = (store: ApplicationState): FormCreatorState => store.formCreator;

export const getFormCreatorTemplateFilesStore = (store: ApplicationState): FilesType => store.formCreator.template.files;

export const getFormCreatorGalleryOpenStore = (state: ApplicationState): boolean => state?.formCreator?.isGalleryOpen;
